import clsx from "clsx";
import { enqueueSnackbar } from "notistack";
import { Controller, useForm } from "react-hook-form";

import FormLabel from "~/components/login/formLabel/FormLabel";
import Select from "~/components/mantine/select/Select";
import Button from "~/components/ui/button/Button";
import Input from "~/components/ui/input/Input";
import { messageFromError } from "~/helpers/messageFromError";
import { tryCatch } from "~/helpers/tryCatch";

import {
  DecodedWidgetConfig,
  ViewType,
  ViewTypeSchema,
} from "../../types/DecodedWidgetConfig.type";
import styles from "./WidgetSettingsForm.module.scss";

type WidgetSettings = {
  title: string;
  viewType: ViewType;
};

export const WidgetSettingsForm = ({
  className,
  config,
  updateConfig,
  onEditingCompleted,
}: {
  className?: string;
  config: DecodedWidgetConfig;
  updateConfig: (config: DecodedWidgetConfig) => Promise<void>;
  onEditingCompleted: () => void;
}) => {
  const { control, register, handleSubmit, formState } =
    useForm<WidgetSettings>({
      defaultValues: {
        title: config.metadata.title,
        viewType: config.metadata.viewType,
      },
    });

  const onSubmit = async (data: WidgetSettings) => {
    const result = await tryCatch(
      updateConfig({
        ...config,
        metadata: {
          ...config.metadata,
          ...data,
        },
      }),
    );

    if (result.error) {
      enqueueSnackbar(messageFromError(result.error), {
        variant: "error",
        persist: true,
      });

      return;
    }

    onEditingCompleted();
  };

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      className={clsx(styles.form, className)}
    >
      <div className={styles.formRow}>
        <FormLabel label="Title" />
        <Input {...register("title")} disabled={formState.isSubmitting} />
      </div>
      <div className={styles.formRow}>
        <FormLabel label="View type" />
        <Controller
          control={control}
          name="viewType"
          rules={{ required: "this field is required" }}
          render={({ field, fieldState }) => {
            return (
              <Select
                data={ViewTypeSchema.options}
                searchable={false}
                disabled={formState.isSubmitting}
                {...field}
                error={fieldState.error?.message}
              />
            );
          }}
        />
      </div>
      <div className={styles.actionsRow}>
        <Button
          type="button"
          outlined
          disabled={formState.isSubmitting}
          onClick={() => {
            onEditingCompleted();
          }}
        >
          Cancel
        </Button>
        <Button
          withSpinner={formState.isSubmitting}
          disabled={formState.isSubmitting}
          type="submit"
        >
          Save
        </Button>
      </div>
    </form>
  );
};
